import PropTypes from 'prop-types';
import { Card, Spinner, Avatar } from '@components/ui';
import { BurndownChart, VelocityChart } from '@components/features';
import { useTasks, useSprints, useProjectMetrics, useTeamWorkload } from '@hooks';
import { formatDay } from '@utils/calendarDate';

function StatCard({ label, value, change, icon, color }) {
  const cardStyle = {
    backgroundColor: 'white',
    borderRadius: '16px',
    padding: '20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    border: '1px solid rgba(226, 232, 240, 0.5)',
  };

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <p style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>{label}</p>
          <h3 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1e293b', margin: 0 }}>{value}</h3>
          {change && (
            <p style={{ fontSize: '12px', marginTop: '4px', color, margin: 0 }}>
              {change}
            </p>
          )}
        </div>
        <div
          style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px',
            backgroundColor: color + '15',
          }}
        >
          {icon}
        </div>
      </div>
    </div>
  );
}

StatCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  change: PropTypes.string,
  icon: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
};

function ProgressBar({ label, value, max, color }) {
  const percentage = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div style={{ marginBottom: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', marginBottom: '6px' }}>
        <span style={{ color: '#475569' }}>{label}</span>
        <span style={{ color: '#64748b' }}>{value} / {max} ({percentage}%)</span>
      </div>
      <div style={{ width: '100%', backgroundColor: '#e2e8f0', borderRadius: '4px', height: '8px' }}>
        <div
          style={{
            height: '8px',
            borderRadius: '4px',
            width: `${percentage}%`,
            backgroundColor: color,
            transition: 'width 0.3s ease',
          }}
        />
      </div>
    </div>
  );
}

ProgressBar.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  color: PropTypes.string.isRequired,
};

export default function MetricsView({ projectId }) {
  const { data: tasksData, isLoading: tasksLoading } = useTasks({ projectId }, { enabled: !!projectId });
  const { data: sprintsData, isLoading: sprintsLoading } = useSprints({ projectId }, { enabled: !!projectId });
  const { data: workloadData } = useTeamWorkload(projectId);

  const tasks = tasksData?.data || [];
  const sprints = sprintsData?.data || [];
  const teamWorkload = workloadData?.data || [];

  const isLoading = tasksLoading || sprintsLoading;

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '256px' }}>
        <Spinner size="lg" />
      </div>
    );
  }

  // Calculate metrics
  const totalTasks = tasks.length;
  const completedTasks = tasks.filter(t => t.status === 'done').length;
  const inProgressTasks = tasks.filter(t => t.status === 'progress').length;
  const reviewTasks = tasks.filter(t => t.status === 'review').length;
  const backlogTasks = tasks.filter(t => t.status === 'backlog' || t.status === 'todo').length;

  const totalPoints = tasks.reduce((sum, t) => sum + (t.points || 0), 0);
  const completedPoints = tasks.filter(t => t.status === 'done').reduce((sum, t) => sum + (t.points || 0), 0);

  const totalHours = tasks.reduce((sum, t) => sum + (t.hours || 0), 0);
  const completedHours = tasks.filter(t => t.status === 'done').reduce((sum, t) => sum + (t.hours || 0), 0);

  const activeSprint = sprints.find(s => s.status === 'ACTIVE');
  const completedSprints = sprints.filter(s => s.status === 'COMPLETED').length;

  // Task distribution by priority
  const tasksByPriority = tasks.reduce((acc, task) => {
    const priority = (task.priority || 'MEDIUM').toUpperCase();
    acc[priority] = (acc[priority] || 0) + 1;
    return acc;
  }, {});

  const priorityColors = {
    CRITICAL: '#ef4444',
    HIGH: '#f59e0b',
    MEDIUM: '#3b82f6',
    LOW: '#10b981',
  };

  const priorityLabels = {
    CRITICAL: 'Crítica',
    HIGH: 'Alta',
    MEDIUM: 'Media',
    LOW: 'Baja',
  };

  // Task distribution by type
  const tasksByType = tasks.reduce((acc, task) => {
    const type = (task.type || 'TASK').toUpperCase();
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {});

  const typeLabels = {
    FEATURE: 'Feature',
    BUG: 'Bug',
    IMPROVEMENT: 'Mejora',
    TASK: 'Tarea',
  };

  return (
    <div>
      <h1 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1e293b', marginBottom: '24px' }}>
        Métricas del Proyecto
      </h1>

      {/* Main Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        <StatCard
          label="Total Tareas"
          value={totalTasks}
          change={`${completedTasks} completadas`}
          icon="📋"
          color="#667eea"
        />
        <StatCard
          label="En Progreso"
          value={inProgressTasks + reviewTasks}
          change={`${reviewTasks} en revisión`}
          icon="⚡"
          color="#f59e0b"
        />
        <StatCard
          label="Story Points"
          value={totalPoints}
          change={`${completedPoints} entregados`}
          icon="🎯"
          color="#8b5cf6"
        />
        <StatCard
          label="Horas Estimadas"
          value={totalHours}
          change={`${Math.round(completedHours)}h completadas`}
          icon="⏱️"
          color="#10b981"
        />
      </div>

      {/* Charts Row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '24px', marginBottom: '24px' }}>
        <BurndownChart sprintId={activeSprint?.id} height={280} />
        <VelocityChart projectId={projectId} height={280} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '24px', marginBottom: '24px' }}>
        {/* Progress Overview */}
        <Card padding="lg">
          <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>
            Progreso General
          </h3>
          <ProgressBar
            label="Tareas Completadas"
            value={completedTasks}
            max={totalTasks}
            color="#10b981"
          />
          <ProgressBar
            label="Story Points"
            value={completedPoints}
            max={totalPoints}
            color="#8b5cf6"
          />
          <ProgressBar
            label="Horas Trabajadas"
            value={Math.round(completedHours)}
            max={Math.round(totalHours)}
            color="#3b82f6"
          />
        </Card>

        {/* Sprint Info */}
        <Card padding="lg">
          <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>
            Estado de Sprints
          </h3>
          {activeSprint ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: '#475569', fontSize: '14px' }}>Sprint Activo</span>
                <span style={{ fontWeight: '600', color: '#667eea', fontSize: '14px' }}>{activeSprint.name}</span>
              </div>
              {activeSprint.startDate && activeSprint.endDate && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#475569', fontSize: '14px' }}>Duración</span>
                  <span style={{ fontSize: '13px', color: '#64748b' }}>
                    {formatDay(activeSprint.startDate)} - {formatDay(activeSprint.endDate)}
                  </span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: '#475569', fontSize: '14px' }}>Sprints Completados</span>
                <span style={{ fontWeight: '500', fontSize: '14px' }}>{completedSprints}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: '#475569', fontSize: '14px' }}>Total Sprints</span>
                <span style={{ fontWeight: '500', fontSize: '14px' }}>{sprints.length}</span>
              </div>
            </div>
          ) : (
            <p style={{ color: '#64748b', fontSize: '14px' }}>No hay sprint activo actualmente</p>
          )}
        </Card>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
        {/* Distribution by Status */}
        <Card padding="lg">
          <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>
            Distribución por Estado
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {[
              { label: 'Backlog / Por hacer', value: backlogTasks, color: '#94a3b8' },
              { label: 'En Progreso', value: inProgressTasks, color: '#f59e0b' },
              { label: 'En Revisión', value: reviewTasks, color: '#8b5cf6' },
              { label: 'Completadas', value: completedTasks, color: '#10b981' },
            ].map((item) => (
              <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: item.color,
                    flexShrink: 0,
                  }}
                />
                <span style={{ flex: 1, fontSize: '14px', color: '#475569' }}>{item.label}</span>
                <span style={{ fontWeight: '600', color: '#1e293b', fontSize: '14px' }}>{item.value}</span>
                <span style={{ fontSize: '12px', color: '#94a3b8', width: '48px', textAlign: 'right' }}>
                  {totalTasks > 0 ? Math.round((item.value / totalTasks) * 100) : 0}%
                </span>
              </div>
            ))}
          </div>
        </Card>

        {/* Distribution by Priority */}
        <Card padding="lg">
          <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>
            Distribución por Prioridad
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {Object.entries(priorityColors).map(([priority, color]) => {
              const count = tasksByPriority[priority] || 0;
              return (
                <div key={priority} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div
                    style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      backgroundColor: color,
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ flex: 1, fontSize: '14px', color: '#475569' }}>
                    {priorityLabels[priority]}
                  </span>
                  <span style={{ fontWeight: '600', color: '#1e293b', fontSize: '14px' }}>{count}</span>
                  <span style={{ fontSize: '12px', color: '#94a3b8', width: '48px', textAlign: 'right' }}>
                    {totalTasks > 0 ? Math.round((count / totalTasks) * 100) : 0}%
                  </span>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Distribution by Type */}
        <Card padding="lg">
          <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>
            Distribución por Tipo
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            {Object.entries(typeLabels).map(([type, label]) => {
              const count = tasksByType[type] || 0;
              return (
                <div key={type} style={{ textAlign: 'center', padding: '16px', backgroundColor: '#f8fafc', borderRadius: '12px' }}>
                  <p style={{ fontSize: '28px', fontWeight: 'bold', color: '#1e293b', margin: 0 }}>{count}</p>
                  <p style={{ fontSize: '12px', color: '#64748b', margin: '4px 0 0 0' }}>{label}</p>
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      {/* Team Workload Section */}
      {teamWorkload.length > 0 && (
        <div style={{ marginTop: '24px' }}>
          <Card padding="lg">
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '20px' }}>
              Carga de Trabajo del Equipo
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {teamWorkload.map((member) => {
                const memberName = member.name || member.user?.name;
                const initials = memberName
                  ?.split(' ')
                  .map((n) => n[0])
                  .join('')
                  .toUpperCase() || '??';
                const utilization = member.utilizationRate ?? member.utilization ?? 0;
                const utilizationColor =
                  utilization > 100 ? '#ef4444' :
                  utilization > 80 ? '#f59e0b' :
                  utilization > 50 ? '#10b981' : '#94a3b8';

                return (
                  <div
                    key={member.userId}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      padding: '12px 16px',
                      backgroundColor: '#f8fafc',
                      borderRadius: '12px',
                    }}
                  >
                    <Avatar initials={initials} size="sm" />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                        <span style={{ fontSize: '14px', fontWeight: '500', color: '#1e293b' }}>
                          {memberName || 'Usuario'}
                        </span>
                        <span
                          style={{
                            fontSize: '12px',
                            fontWeight: '600',
                            color: utilizationColor,
                            backgroundColor: utilizationColor + '15',
                            padding: '2px 8px',
                            borderRadius: '6px',
                          }}
                        >
                          {Math.round(utilization)}% utilización
                        </span>
                      </div>
                      <div style={{ width: '100%', backgroundColor: '#e2e8f0', borderRadius: '4px', height: '6px' }}>
                        <div
                          style={{
                            height: '6px',
                            borderRadius: '4px',
                            width: `${Math.min(utilization, 100)}%`,
                            backgroundColor: utilizationColor,
                            transition: 'width 0.3s ease',
                          }}
                        />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: '#64748b' }}>
                      <span title="Tareas asignadas">📋 {member.assignedTasks || member.taskCount || 0}</span>
                      <span title="Story Points">🎯 {member.totalPoints || 0}</span>
                      <span title="Horas asignadas">⏱️ {member.totalHours || 0}h</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

MetricsView.propTypes = {
  projectId: PropTypes.number,
};
